import React, { useState, useMemo, useEffect } from 'react';
import { Box, Card, Typography, Select, MenuItem, OutlinedInput } from '@mui/material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const INPUT_TYPES = [
  { key: 'fertilizer', label: 'Fertilizer' },
  { key: 'fungicide', label: 'Fungicide' },
  { key: 'insecticide', label: 'Insecticide' },
  { key: 'herbicide', label: 'Herbicide' },
  { key: 'seed', label: 'Seed' },
];
const COLORS = ['#F9AA33', '#4CAF50', '#2196F3', '#9C27B0', '#FF5722'];

const UNIT_CONVERSIONS = {
  LBS: amt => amt * 0.453592,
  G: amt => amt / 1000,
  mL: amt => amt / 1_000_000,
  L: amt => amt / 1000,
  OZ: amt => amt * 0.0283495,
  KGS: amt => amt,
};

function convertToKg(amt, unit) {
  if (!amt || !unit) return 0;
  const fn = UNIT_CONVERSIONS[unit.toUpperCase()];
  return fn ? fn(amt) : 0;
}

const InputUsageChart = ({
  fertilizerEvents = [],
  fungicideEvents = [],
  insecticideEvents = [],
  herbicideEvents = [],
  crops = [],
  sections = [],
}) => {
  // Global toggles
  const [valueType, setValueType] = useState('amount'); // 'amount' | 'cost'
  const [unitType, setUnitType] = useState('raw'); // 'raw' | 'sqm' | 'ha'
  const [viewType, setViewType] = useState('total'); // 'total' | 'section'
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedSection, setSelectedSection] = useState('');

  // Get all years from event data and crops data
  const allYears = useMemo(() => {
    const years = new Set();
    [...fertilizerEvents, ...fungicideEvents, ...insecticideEvents, ...herbicideEvents].forEach(e => {
      if (e.date) years.add(new Date(e.date).getFullYear());
    });
    // Add years from crops planting dates
    crops.forEach(crop => {
      if (crop.planting_date) years.add(new Date(crop.planting_date).getFullYear());
    });
    const result = Array.from(years).sort();
    console.log('Available years:', result);
    console.log('Event counts:', {
      fertilizer: fertilizerEvents.length,
      fungicide: fungicideEvents.length,
      insecticide: insecticideEvents.length,
      herbicide: herbicideEvents.length,
      crops: crops.length
    });
    return result;
  }, [fertilizerEvents, fungicideEvents, insecticideEvents, herbicideEvents, crops]);

  useEffect(() => {
    if (allYears.length > 0 && !selectedYear) {
      const currentYear = new Date().getFullYear();
      // Use current year if available, otherwise use the first available year
      setSelectedYear(allYears.includes(currentYear) ? currentYear : allYears[0]);
    }
  }, [allYears, selectedYear]);

  // Get all sections with names
  const namedSections = useMemo(() => sections.filter(s => s.name), [sections]);
  useEffect(() => {
    if (viewType === 'section' && namedSections.length > 0 && !selectedSection) setSelectedSection(namedSections[0].id);
  }, [viewType, namedSections, selectedSection]);

  // Get total area (for per sqm/ha)
  const totalArea = useMemo(() =>
    sections.reduce((sum, s) => sum + (s.area || s.zone_size || 0), 0)
  , [sections]);

  // Process events for total view
  const pieData = useMemo(() => {
    if (!selectedYear || viewType !== 'total') return [];
    
    const processEvents = (events, type) => {
      const filteredEvents = events.filter(e => new Date(e.date).getFullYear() === Number(selectedYear));
      console.log(`${type} events for year ${selectedYear}:`, filteredEvents.length, filteredEvents);
      
      return filteredEvents.reduce((sum, event) => {
          let val = valueType === 'amount' 
          ? (event.amt_used_kg || 0)
            : (event.event_cost || 0);
          
          if (unitType === 'sqm' && totalArea > 0) val = val / totalArea;
          if (unitType === 'ha' && totalArea > 0) val = (val / totalArea) * 10000;
          
          return sum + val;
        }, 0);
    };

    const processSeedCosts = () => {
      const filteredCrops = crops.filter(crop => new Date(crop.planting_date).getFullYear() === Number(selectedYear));
      console.log(`seed crops for year ${selectedYear}:`, filteredCrops.length, filteredCrops);
      
      return filteredCrops.reduce((sum, crop) => {
        let val = valueType === 'amount' 
          ? (crop.units_planted || 0)
          : (crop.seed_cost || 0);
        
        if (unitType === 'sqm' && totalArea > 0) val = val / totalArea;
        if (unitType === 'ha' && totalArea > 0) val = (val / totalArea) * 10000;
        
        return sum + val;
      }, 0);
    };

    const result = INPUT_TYPES.map(({ key, label }, idx) => {
      // Skip seed if valueType is 'amount' since seed isn't measured in kgs
      if (key === 'seed' && valueType === 'amount') {
        return null;
      }
      
      let value = 0;
      
      if (key === 'seed') {
        value = processSeedCosts();
      } else {
        const events = {
          fertilizer: fertilizerEvents,
          fungicide: fungicideEvents,
          insecticide: insecticideEvents,
          herbicide: herbicideEvents,
        }[key];
        value = processEvents(events, key);
      }
      
      console.log(`${key} total value:`, value);
      
      return {
        name: label,
        value: Number(value.toFixed(2)),
        fill: COLORS[idx % COLORS.length],
      };
    }).filter(d => d && d.value > 0);
    
    console.log('Final pieData:', result);
    return result;
  }, [selectedYear, viewType, valueType, unitType, totalArea, fertilizerEvents, fungicideEvents, insecticideEvents, herbicideEvents, crops]);

  // Process events for section view
  const barData = useMemo(() => {
    if (!selectedYear || viewType !== 'section') return [];
    
    const processEvents = (events, type, zoneId) => {
      return events
        .filter(e => {
          return new Date(e.date).getFullYear() === Number(selectedYear) && e.zone_id === zoneId;
        })
        .reduce((sum, event) => {
          let val = valueType === 'amount' 
            ? (event.amt_used_kg || 0)
            : (event.event_cost || 0);
          
          const zone = sections.find(s => s.id === zoneId);
          const zoneArea = zone?.area || 0;
          
          if (unitType === 'sqm' && zoneArea > 0) val = val / zoneArea;
          if (unitType === 'ha' && zoneArea > 0) val = (val / zoneArea) * 10000;
          
          return sum + val;
        }, 0);
    };

    const processSeedCosts = (zoneId) => {
      return crops
        .filter(crop => {
          return new Date(crop.planting_date).getFullYear() === Number(selectedYear) && crop.zone_id === zoneId;
        })
        .reduce((sum, crop) => {
          let val = valueType === 'amount' 
            ? (crop.units_planted || 0)
            : (crop.seed_cost || 0);
          
          const zone = sections.find(s => s.id === zoneId);
          const zoneArea = zone?.area || 0;
          
          if (unitType === 'sqm' && zoneArea > 0) val = val / zoneArea;
          if (unitType === 'ha' && zoneArea > 0) val = (val / zoneArea) * 10000;
          
          return sum + val;
        }, 0);
    };

    return namedSections.map(section => {
      const entry = { section: section.name };
      INPUT_TYPES.forEach(({ key }) => {
        // Skip seed if valueType is 'amount' since seed isn't measured in kgs
        if (key === 'seed' && valueType === 'amount') {
          entry[key] = 0;
          return;
        }
        
        if (key === 'seed') {
          entry[key] = Number(processSeedCosts(section.id).toFixed(2));
        } else {
          const events = {
            fertilizer: fertilizerEvents,
            fungicide: fungicideEvents,
            insecticide: insecticideEvents,
            herbicide: herbicideEvents,
          }[key];
          entry[key] = Number(processEvents(events, key, section.id).toFixed(2));
        }
      });
      return entry;
    });
  }, [selectedYear, viewType, valueType, unitType, sections, namedSections, fertilizerEvents, fungicideEvents, insecticideEvents, herbicideEvents, crops]);

  // Debug: log insecticide events for the selected year
  useEffect(() => {
    if (!insecticideEvents) return;
    const filtered = insecticideEvents.filter(e => new Date(e.date).getFullYear() === Number(selectedYear));
  }, [insecticideEvents, selectedYear]);

  return (
    <Card
      sx={{
        backgroundColor: '#181F2A',
        color: '#fff',
        borderRadius: 1.5,
        p: { xs: 2, md: 3 },
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.20)',
        mb: { xs: 2, md: 2 },
        minHeight: { xs: 350, md: 430 }
      }}
    >
      <Box display="flex" alignItems="center" mb={1}>
        <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 500, fontSize: 15, mr: 2, whiteSpace: 'nowrap', flexShrink: 0 }}>
          Input Usage
        </Typography>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            ml: 5,
            overflowX: { xs: 'auto', md: 'visible' },
            whiteSpace: { xs: 'nowrap', md: 'normal' },
            flex: 1,
            minWidth: 0,
            '&::-webkit-scrollbar': {
              height: 4,
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255,255,255,0.1)',
              borderRadius: 2,
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255,255,255,0.3)',
              borderRadius: 2,
            },
          }}
        >
          <Select
            value={viewType}
            onChange={e => setViewType(e.target.value)}
            size="small"
            sx={{ color: '#fff', bgcolor: '#222C2F', borderRadius: 2, minWidth: 70, fontWeight: 400, height: 28, '.MuiSelect-select': { p: '0.5px 0.5px 0.5px 8px', fontSize: 12 }, flexShrink: 0 }}
          >
            <MenuItem value="total">Total</MenuItem>
            <MenuItem value="section">By Section</MenuItem>
          </Select>
          <Select
            value={valueType}
            onChange={e => setValueType(e.target.value)}
            size="small"
            sx={{ color: '#fff', bgcolor: '#222C2F', borderRadius: 2, minWidth: 70, fontWeight: 400, height: 28, '.MuiSelect-select': { p: '0.5px 0.5px 0.5px 8px', fontSize: 12 }, flexShrink: 0 }}
          >
            <MenuItem value="amount">Amount</MenuItem>
            <MenuItem value="cost">Cost</MenuItem>
          </Select>
          <Select
            value={unitType}
            onChange={e => setUnitType(e.target.value)}
            size="small"
            sx={{ color: '#fff', bgcolor: '#222C2F', borderRadius: 2, minWidth: 70, fontWeight: 400, height: 28, '.MuiSelect-select': { p: '0.5px 0.5px 0.5px 8px', fontSize: 12 }, flexShrink: 0 }}
          >
            <MenuItem value="raw">Raw</MenuItem>
            <MenuItem value="sqm">per m²</MenuItem>
            <MenuItem value="ha">per ha</MenuItem>
          </Select>
          <Select
            value={selectedYear}
            onChange={e => setSelectedYear(e.target.value)}
            size="small"
            sx={{ color: '#fff', bgcolor: '#222C2F', borderRadius: 2, minWidth: 60, fontWeight: 400, height: 28, '.MuiSelect-select': { p: '0.5px 0.5px 0.5px 8px', fontSize: 12 }, flexShrink: 0 }}
          >
            {allYears.map(year => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
          {viewType === 'section' && (
            <Select
              value={selectedSection}
              onChange={e => setSelectedSection(e.target.value)}
              size="small"
              sx={{ display: 'none' }}
              input={<OutlinedInput />}
            >
              {namedSections.map(section => (
                <MenuItem key={section.id} value={section.id}>{section.name}</MenuItem>
              ))}
            </Select>
          )}
        </Box>
      </Box>
      <Typography sx={{ color: '#AAB4C8', fontWeight: 400, fontSize: 12, mb: 1, mt: -1 }}>
        {viewType === 'total' 
          ? `Total input ${valueType === 'amount' ? 'usage' : 'cost'} ${valueType === 'amount' 
            ? unitType === 'raw' ? 'in kg' 
              : unitType === 'sqm' ? 'per m²' 
              : 'in kg per ha'
            : 'in NGN'}`
          : `Input ${valueType === 'amount' ? 'usage' : 'cost'} by section ${valueType === 'amount'
            ? unitType === 'raw' ? 'in kg'
              : unitType === 'sqm' ? 'per m²'
              : 'in kg per ha'
            : 'in NGN'}`}
      </Typography>
      <Box sx={{ width: '100%', flex: 1, minHeight: 280, mt: 1, height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {viewType === 'total' ? (
          pieData.length === 0 ? (
            <Typography sx={{ color: '#AAB4C8', textAlign: 'center', fontSize: 18 }}>No data for this selection.</Typography>
          ) : (
            <Box width="100%" display="flex" flexDirection="column" alignItems="center">
              <PieChart width={320} height={220} style={{ margin: '0 auto' }}>
                <Pie
                  data={pieData}
                  cx={160}
                  cy={110}
                  innerRadius={60}
                  outerRadius={90}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                  isAnimationActive={false}
                  stroke="#222C2F"
                  strokeWidth={2}
                >
                  {pieData.map((entry, idx) => (
                    <Cell key={`cell-${idx}`} fill={entry.fill} />
                  ))}
                </Pie>
              </PieChart>
              {/* Custom legend below chart */}
              <Box display="flex" flexDirection="column" alignItems="center" gap={1} mt={2}>
                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="center" alignItems="center" gap={{ xs: 1, sm: 3 }}>
                  {pieData.slice(0, Math.ceil(pieData.length / 2)).map((entry, idx) => (
                    <Box key={entry.name} display="flex" alignItems="center" gap={1}>
                      <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: entry.fill, mr: 1 }} />
                      <Typography sx={{ color: '#fff', fontWeight: 500, fontSize: 15 }}>{entry.name}</Typography>
                      <Typography sx={{ color: '#F9AA33', fontWeight: 700, fontSize: 15, ml: 0.5 }}>{entry.value}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="center" alignItems="center" gap={{ xs: 1, sm: 3 }}>
                  {pieData.slice(Math.ceil(pieData.length / 2)).map((entry, idx) => (
                    <Box key={entry.name} display="flex" alignItems="center" gap={1}>
                      <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: entry.fill, mr: 1 }} />
                      <Typography sx={{ color: '#fff', fontWeight: 500, fontSize: 15 }}>{entry.name}</Typography>
                      <Typography sx={{ color: '#F9AA33', fontWeight: 700, fontSize: 15, ml: 0.5 }}>{entry.value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )
        ) : (
          barData.length === 0 ? (
            <Typography sx={{ color: '#AAB4C8', textAlign: 'center', fontSize: 18 }}>No data for this selection.</Typography>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={barData} margin={{ top: 16, right: 16, left: 0, bottom: 16 }}>
                <XAxis dataKey="section" tick={{ fill: '#AAB4C8', fontSize: 12 }} interval={0} angle={-30} textAnchor="end" />
                <YAxis tick={{ fill: '#AAB4C8', fontSize: 12 }} />
                {INPUT_TYPES.map(({ key, label }, idx) => (
                  <Bar key={key} dataKey={key} stackId="a" fill={COLORS[idx % COLORS.length]} name={label} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )
        )}
      </Box>
    </Card>
  );
};

export default InputUsageChart; 